import { Link, useNavigate } from "react-router-dom";
import { Brain, HeartHandshake, Eye, Sparkles, ClipboardCheck, LineChart, MessageSquareQuote, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="max-w-7xl mx-auto px-6 py-6 flex items-center gap-4">
        <Logo size="md" testid="landing-brand-logo" />
        <div className="flex-1" />
        {user ? (
          <Button data-testid="cta-open-app" onClick={() => nav(user.role === "patient" ? "/dashboard" : "/pro")}>
            Entrar en mi panel <ArrowRight className="ml-2" size={16} />
          </Button>
        ) : (
          <div className="flex gap-2">
            <Link to="/login"><Button variant="ghost" data-testid="hero-login-btn">Iniciar sesión</Button></Link>
            <Link to="/register"><Button data-testid="hero-register-btn">Crear cuenta</Button></Link>
          </div>
        )}
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-10 pb-24 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider">
            <Sparkles size={14} /> Basado en evidencia · Uso clínico
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="mt-6 heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight"
          >
            Programa de entrenamiento en <span className="text-primary">cognición social</span>.
          </motion.h1>
          <p className="mt-6 text-lg sm:text-xl text-foreground/80 leading-relaxed max-w-2xl">
            Evaluación pre/post, entrenamiento adaptativo en cognición social, clasificación clínica de errores y
            paneles clínicos completos. 20 sesiones estructuradas en 4 módulos, con ejercicios originales
            en texto, ilustraciones, viñetas, audio, mensajes y obras de arte.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" className="rounded-full h-12 px-6 text-base" data-testid="hero-start-btn"
              onClick={() => nav(user ? "/dashboard" : "/register")}
            >
              Empezar evaluación PRE <ArrowRight className="ml-2" size={18} />
            </Button>
            <Button size="lg" variant="outline" className="rounded-full h-12 px-6 text-base"
              data-testid="hero-pro-btn"
              onClick={() => nav("/login")}
            >
              Soy profesional
            </Button>
          </div>

          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
            {[
              { n: "20", t: "sesiones estructuradas" },
              { n: "4", t: "módulos clínicos" },
              { n: "11", t: "dominios evaluados" },
              { n: "8", t: "tipos de error clínico" },
            ].map((s) => (
              <div key={s.t} className="border-l-2 border-primary/40 pl-3">
                <div className="heading text-3xl font-extrabold text-primary">{s.n}</div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{s.t}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="rounded-3xl overflow-hidden border border-border shadow-lg"
          >
            <img
              src="https://images.unsplash.com/photo-1573878587306-c6723c815090?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwZ3JvdXAlMjBvZiUyMHBlb3BsZSUyMHRhbGtpbmd8ZW58MHx8fHwxNzg1NzU1NzU4fDA&ixlib=rb-4.1.0&q=85"
              alt="Grupo diverso conversando"
              className="w-full h-[420px] object-cover"
            />
          </motion.div>
          <div className="absolute -bottom-8 -left-8 bg-card border border-border rounded-2xl p-4 max-w-xs shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/20 text-accent grid place-items-center">
                <HeartHandshake size={22} />
              </div>
              <div>
                <div className="font-semibold text-sm">Feedback clínico inmediato</div>
                <div className="text-xs text-muted-foreground">Cada respuesta explica las claves.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40 py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { Icon: Eye, title: "Reconocimiento emocional", txt: "Rostros, ojos, boca y microexpresiones con ilustraciones originales." },
            { Icon: MessageSquareQuote, title: "Mentalización y ToM", txt: "Creencias, faux pas, ironía, metáforas y doble sentido." },
            { Icon: LineChart, title: "Entrenamiento adaptativo", txt: "Ajuste automático de dificultad y clasificación clínica de errores." },
          ].map(({ Icon, title, txt }) => (
            <div key={title} className="p-6 rounded-2xl bg-card border border-border">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary grid place-items-center mb-4">
                <Icon size={20} />
              </div>
              <h3 className="heading text-xl font-bold mb-2">{title}</h3>
              <p className="text-foreground/75 text-base leading-relaxed">{txt}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-12 items-center">
        <div className="rounded-3xl overflow-hidden border border-border">
          <img
            src="https://images.unsplash.com/photo-1520529277867-dbf8c5e0b340?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxmYWNpYWwlMjBleHByZXNzaW9ucyUyMGVtb3Rpb25zJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg1NzU1NzYyfDA&ixlib=rb-4.1.0&q=85"
            alt="Reconocimiento facial"
            className="w-full h-[380px] object-cover"
          />
        </div>
        <div>
          <div className="text-sm uppercase tracking-widest text-primary font-semibold mb-3">Para profesionales</div>
          <h2 className="heading text-3xl sm:text-4xl font-extrabold leading-tight">Panel clínico completo</h2>
          <p className="mt-5 text-lg text-foreground/80 leading-relaxed">
            Visualiza la evolución longitudinal de cada paciente, tipos de error dominantes, tiempo de respuesta,
            adherencia y perfil cognitivo por dominio. Exporta informes PDF y bases CSV para investigación.
          </p>
          <ul className="mt-6 space-y-3">
            {["Perfil cognitivo por dominio (radar)", "Comparación PRE ↔ POST", "Exportación PDF / CSV / SPSS-ready"].map((f) => (
              <li key={f} className="flex items-start gap-2">
                <ClipboardCheck className="text-primary shrink-0 mt-1" size={18} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-6 text-sm text-muted-foreground flex flex-wrap gap-4 items-center justify-between">
          <span>© 2026 SocialCog Training®. Todos los derechos reservados.</span>
          <span>Contenido 100% original · no sustituye evaluación clínica presencial</span>
        </div>
      </footer>
    </div>
  );
}
